import React from 'react';
import { ScrollArea } from '@/lib/components/ui/scroll-area';
import { Badge } from '@/lib/components/ui/badge';
import { Users, Calendar, CalendarDays, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

// PermissionDto structure
export interface PermissionDto {
  roles?: string[];
  week_days?: number[];
  month_days?: number[];
  hours?: [number, number];
  action?: 'READ' | 'UPDATE' | 'DELETE';
  private?: boolean;
}

export type AttributeType = 'roles' | 'week_days' | 'month_days' | 'hours';

interface AbacSidebarProps {
  selectedAttribute: AttributeType;
  onAttributeSelect: (attribute: AttributeType) => void;
  selectedCounts: {
    roles: number;
    week_days: number;
    month_days: number;
    hours: number;
  };
}

export const AbacSidebar: React.FC<AbacSidebarProps> = ({ selectedAttribute, onAttributeSelect, selectedCounts }) => {
  const attributes = [
    {
      id: 'roles' as const,
      label: 'Roles',
      description: 'Select user roles',
      icon: Users,
      color: 'blue',
    },
    {
      id: 'week_days' as const,
      label: 'Week Days',
      description: 'Choose days of the week',
      icon: Calendar,
      color: 'green',
    },
    {
      id: 'month_days' as const,
      label: 'Month Days',
      description: 'Select days of the month',
      icon: CalendarDays,
      color: 'purple',
    },
    {
      id: 'hours' as const,
      label: 'Hours',
      description: 'Set time restrictions',
      icon: Clock,
      color: 'orange',
    },
  ];

  const getColorClasses = (color: string, isSelected: boolean) => {
    const colors = {
      blue: {
        selected: 'border-blue-500/50 bg-gradient-to-br from-blue-50 to-blue-100/50 shadow-blue-100',
        unselected: 'hover:border-blue-300 hover:bg-blue-50/30',
        icon: 'text-blue-600',
        badge: 'bg-blue-500 text-white',
      },
      green: {
        selected: 'border-green-500/50 bg-gradient-to-br from-green-50 to-green-100/50 shadow-green-100',
        unselected: 'hover:border-green-300 hover:bg-green-50/30',
        icon: 'text-green-600',
        badge: 'bg-green-500 text-white',
      },
      purple: {
        selected: 'border-purple-500/50 bg-gradient-to-br from-purple-50 to-purple-100/50 shadow-purple-100',
        unselected: 'hover:border-purple-300 hover:bg-purple-50/30',
        icon: 'text-purple-600',
        badge: 'bg-purple-500 text-white',
      },
      orange: {
        selected: 'border-orange-500/50 bg-gradient-to-br from-orange-50 to-orange-100/50 shadow-orange-100',
        unselected: 'hover:border-orange-300 hover:bg-orange-50/30',
        icon: 'text-orange-600',
        badge: 'bg-orange-500 text-white',
      },
    };
    return colors[color as keyof typeof colors];
  };

  return (
    <div className="animate-fade-in flex w-80 flex-col rounded-r-xl border-r border-blue-200 bg-white">
      {/* Header */}

      <ScrollArea className="flex-1">
        <div className="space-y-3 p-6">
          {attributes.map((attribute, index) => {
            const isSelected = selectedAttribute === attribute.id;
            const colorClasses = getColorClasses(attribute.color, isSelected);
            const Icon = attribute.icon;

            return (
              <button
                key={attribute.id}
                onClick={() => onAttributeSelect(attribute.id)}
                className={cn(
                  'group animate-fade-in w-full rounded-xl border-2 bg-white/60 p-5 text-left backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/5',
                  isSelected
                    ? `${colorClasses.selected} scale-[1.02] transform shadow-lg`
                    : `border-gray-200/50 ${colorClasses.unselected}`,
                )}
                style={{ animationDelay: `${index * 100 + 200}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-xl transition-all duration-300',
                      isSelected
                        ? `bg-white shadow-lg ${colorClasses.icon}`
                        : 'bg-gray-100 text-gray-400 group-hover:bg-white group-hover:shadow-md',
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <h3
                        className={cn(
                          'font-semibold transition-colors',
                          isSelected ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900',
                        )}
                      >
                        {attribute.label}
                      </h3>
                      {selectedCounts[attribute.id] > 0 && (
                        <div
                          className={cn(
                            'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold shadow-sm transition-all duration-300',
                            isSelected ? colorClasses.badge : 'bg-gray-400 text-white group-hover:bg-gray-500',
                          )}
                        >
                          {selectedCounts[attribute.id]}
                        </div>
                      )}
                    </div>
                    <p
                      className={cn(
                        'mt-1 text-xs leading-relaxed transition-colors',
                        isSelected ? 'text-gray-600' : 'text-gray-500',
                      )}
                    >
                      {attribute.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
